import React, { useMemo, useState } from 'react';

import { countItemsWithTag, tagAssociationDelta } from '@libs/NwUtils';

import useTagCatalog, { useAdjustTagUsageCount } from '@hooks/CachedLists/UseTagCatalog';

import TagPickerPanel, { DRAWER_PANEL_MAX_HEIGHT } from '@components/TagPicker/TagPickerPanel';

/**
 * Applicazione o rimozione di un tag su una selezione multipla (footer delle wall).
 * Non c'e' un insieme "applicato": un tag puo' stare su alcuni degli oggetti selezionati e
 * non su altri, quindi la lista e' piatta e ogni riga fa una sola azione su tutta la selezione.
 *
 * selectedItems  record selezionati, ognuno con i tag in `T`
 * mode           'add' propone i tag non ancora su tutti, 'remove' quelli presenti su almeno uno
 *
 * Il drawer e il counter (quanti selezionati hanno il tag) restano di questo componente:
 * la preferenza "show always in drawer" non passa da qui e non usa UsageCount del catalogo.
 */
const BulkTagPicker = ({ itemType, selectedItems, mode, onAddTag, onRemoveTag, outerLoading }) => {
    const catalog = useTagCatalog(itemType);
    const adjustTagUsageCount = useAdjustTagUsageCount();
    const [isSaving, setIsSaving] = useState(false);
    const isAddMode = mode === 'add';

    const options = useMemo(() => {
        // Quante fra le selezionate hanno ciascun tag. Il conteggio del catalogo (UsageCount)
        // riguarda tutti gli oggetti: qui interessa solo la selezione.
        return (
            catalog
                .map((tag) => ({ tag, count: countItemsWithTag(selectedItems, tag) }))
                // in aggiunta: i tag che non sono gia' su tutte. In rimozione: quelli su almeno una
                .filter(({ count }) => (isAddMode ? count < selectedItems.length : count > 0))
                .map(({ tag, count }) => ({
                    key: tag.ID,
                    name: tag.Name,
                    usageCount: count,
                    isPrivate: tag.isPrivate,
                    tag,
                }))
        );
    }, [catalog, selectedItems, isAddMode]);

    const handleToggle = async (option) => {
        setIsSaving(true);
        const delta = tagAssociationDelta(selectedItems, option.tag, isAddMode ? 'add' : 'remove');
        try {
            await (isAddMode ? onAddTag(option.tag) : onRemoveTag(option.tag));
            adjustTagUsageCount(option.tag.ID, delta);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <TagPickerPanel
            // il drawer lo possiede il footer della wall, quindi qui il panel non passa da
            // TagPicker e l'altezza va data esplicitamente, altrimenti resta ai 24rem di default
            maxHeight={DRAWER_PANEL_MAX_HEIGHT}
            options={options}
            selected={[]}
            onToggle={handleToggle}
            rowIcon={isAddMode ? 'circle-plus' : 'circle-minus'}
            emptyMessage={isAddMode ? 'Every tag is already on the selection' : 'The selection has no tags to remove'}
            loading={isSaving || outerLoading}
        />
    );
};

export default BulkTagPicker;
